import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import Modal from '../../components/Modal/Modal';
import { useI18n } from '../../i18n/I18nContext';
import { pluralKey } from '../../utils/plural';
import { parseBulkQuestions } from './bulkQuestions';
import { OPTION_KEYS } from './quizQuestions';
import './BulkQuestionsDialog.css';

/**
 * "Ro'yxatdan qo'shish": the teacher types or pastes a whole list of questions as text and sees, while
 * typing, which questions are ready and which lines have a problem. Only a list without problems can be
 * added, so nothing half right lands in the quiz. `onAdd` receives the parsed questions
 * ({ text, options, correctIndex }) to append after the `existingCount` questions already there.
 */
export default function BulkQuestionsDialog({ open, existingCount = 0, onClose, onAdd }) {
  const { t, lang } = useI18n();
  const [text, setText] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open) setText('');
  }, [open]);

  const { questions, errors } = useMemo(() => parseBulkQuestions(text), [text]);
  const canAdd = questions.length > 0 && errors.length === 0;

  // An error names a line: clicking it selects that line in the text so it can be fixed in place
  const goToLine = (line) => {
    const el = textareaRef.current;
    if (!el) return;
    const rows = el.value.split('\n');
    const start = rows.slice(0, line - 1).reduce((sum, row) => sum + row.length + 1, 0);
    el.focus();
    el.setSelectionRange(start, start + (rows[line - 1] || '').length);
    const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 22;
    el.scrollTop = Math.max(0, (line - 3) * lineHeight);
  };

  const submit = () => {
    if (canAdd) onAdd(questions);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      closeOnBackdrop={false}
      title={t('quizAuthoring.bulk.title')}
      className="bulkq-modal"
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose}>{t('quizAuthoring.bulk.cancel')}</button>
          <button type="button" className="ds-btn ds-btn-primary" onClick={submit} disabled={!canAdd}>
            {questions.length > 0
              ? t(pluralKey('quizAuthoring.bulk.add', questions.length, lang), { count: questions.length })
              : t('quizAuthoring.bulk.addNothing')}
          </button>
        </>
      )}
    >
      <p className="bulkq-intro">{t('quizAuthoring.bulk.intro')}</p>
      <div className="bulkq-grid">
        <div className="bulkq-input">
          <label className="cq-label" htmlFor="bulkq-text">{t('quizAuthoring.bulk.textareaLabel')}</label>
          <textarea
            id="bulkq-text"
            ref={textareaRef}
            data-autofocus
            className="bulkq-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('quizAuthoring.bulk.placeholder')}
            spellCheck={false}
            rows={14}
          />
          <div className="bulkq-rules">
            <span className="bulkq-rules-title">{t('quizAuthoring.bulk.rulesTitle')}</span>
            <ul>
              <li>{t('quizAuthoring.bulk.ruleQuestion')}</li>
              <li>{t('quizAuthoring.bulk.ruleOptions')}</li>
              <li>{t('quizAuthoring.bulk.ruleCorrect')}</li>
              <li>{t('quizAuthoring.bulk.ruleBlank')}</li>
            </ul>
          </div>
        </div>

        <section className="bulkq-preview" aria-label={t('quizAuthoring.bulk.previewTitle')}>
          <div className="bulkq-preview-head">
            <span className="cq-label">{t('quizAuthoring.bulk.previewTitle')}</span>
            <span className="bulkq-counts" aria-live="polite">
              {questions.length > 0 && (
                <span className="ds-badge ds-badge-success">
                  {t(pluralKey('quizAuthoring.bulk.readyCount', questions.length, lang), { count: questions.length })}
                </span>
              )}
              {errors.length > 0 && (
                <span className="ds-badge ds-badge-warning">
                  {t(pluralKey('quizAuthoring.bulk.errorsCount', errors.length, lang), { count: errors.length })}
                </span>
              )}
            </span>
          </div>

          {errors.length > 0 && (
            <div className="ui-alert bulkq-errors" role="alert">
              <TriangleAlert aria-hidden="true" />
              <div>
                <p className="bulkq-errors-lead">{t('quizAuthoring.bulk.fixErrorsFirst')}</p>
                <ul>
                  {errors.map((err) => (
                    <li key={`${err.line}-${err.code}`}>
                      <button type="button" className="bulkq-error-link" onClick={() => goToLine(err.line)}>
                        {t(`quizAuthoring.bulk.errors.${err.code}`, { line: err.line })}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {questions.length === 0 ? (
            errors.length === 0 && <p className="bulkq-empty">{t('quizAuthoring.bulk.previewEmpty')}</p>
          ) : (
            <ol className="bulkq-list">
              {questions.map((q, i) => (
                <li key={q.line} className="bulkq-q">
                  <p className="bulkq-q-title">
                    <span className="bulkq-q-num">{t('courseAssess.quizzes.questionTitle', { index: existingCount + i + 1 })}</span>
                    <span className="bulkq-q-text">{q.text}</span>
                  </p>
                  <ul className="bulkq-q-opts">
                    {q.options.map((opt, oi) => (
                      <li key={OPTION_KEYS[oi]} className={oi === q.correctIndex ? 'is-correct' : undefined}>
                        <span className="bulkq-q-letter" aria-hidden="true">{OPTION_KEYS[oi]}</span>
                        <span className="bulkq-q-opt">{opt}</span>
                        {oi === q.correctIndex && <span className="ds-badge ds-badge-success">{t('quizAuthoring.bulk.correctTag')}</span>}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </Modal>
  );
}
